/**
 * jadeui/src/pages/MCPChat.jsx
 * 
 * MCP Agent Chat Page - Fixed scrolling + Modern UI
 */
import { useState, useRef, useEffect } from 'react';
import { useMCPClient, useMCPAgent } from '../api/useMCP';
import MCPServerPanel from '../components/MCPServerPanel';
import MCPToolList from '../components/MCPToolList';
import MCPMessage from '../components/MCPMessage';

export default function MCPChat() {
  const { servers, tools, isLoading: serversLoading, connectStdio, disconnect, refresh } = useMCPClient();
  const { messages, isLoading, models, currentModel, sendQuery, clearHistory, changeModel } = useMCPAgent();
  
  const [input, setInput] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [input]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    const query = input.trim();
    setInput('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    try {
      await sendQuery(query);
    } catch (err) {
      console.error('Query failed:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const quickPrompts = [
    { icon: '🔧', text: 'What tools are available?' },
    { icon: '📁', text: 'List files in the directory' },
    { icon: '💡', text: 'Help me get started' },
    { icon: '🧠', text: 'Remember this for later' },
  ];

  return (
    <div className="mcp-chat-page">
      {/* Animated Background */}
      <div className="mcp-bg-gradient"></div>
      <div className="mcp-bg-grid"></div>
      
      {/* Sidebar */}
      <aside className={`mcp-sidebar ${showSidebar ? 'open' : 'closed'}`}>
        <div className="mcp-sidebar-header">
          <div className="mcp-logo">
            <span className="mcp-logo-icon">⚡</span>
            <span className="mcp-logo-text">MCP Agent</span>
          </div>
          <div className="mcp-badge-container">
            <span className="mcp-badge servers">{servers.length} servers</span>
            <span className="mcp-badge tools">{tools.length} tools</span>
          </div>
        </div>

        <div className="mcp-sidebar-content">
          {/* Server Panel */}
          <MCPServerPanel
            servers={servers}
            isLoading={serversLoading}
            onConnect={connectStdio}
            onDisconnect={disconnect}
            onRefresh={refresh}
          />

          {/* Tools List */}
          <MCPToolList tools={tools} servers={servers} />
        </div>

        <div className="mcp-sidebar-footer">
          <div className="mcp-model-info">
            <span className="mcp-model-label">Model</span>
            <span className="mcp-model-name">{currentModel || 'Not connected'}</span>
          </div>
        </div>
      </aside>

      {/* Toggle Sidebar Button */}
      <button 
        className="mcp-sidebar-toggle"
        onClick={() => setShowSidebar(!showSidebar)}
        title={showSidebar ? 'Hide sidebar' : 'Show sidebar'}
      >
        {showSidebar ? '◀' : '▶'}
      </button>

      {/* Main Chat Area */}
      <main className="mcp-main">
        {/* Header */}
        <header className="mcp-header">
          <div className="mcp-header-left">
            <h1>Chat</h1>
            {isLoading && <span className="mcp-status-indicator">Processing...</span>}
          </div>

          <div className="mcp-header-right">
            <div className="mcp-model-select">
              <label>Model:</label>
              <select 
                value={currentModel} 
                onChange={(e) => changeModel(e.target.value)}
                disabled={models.length === 0}
              >
                {models.length === 0 ? (
                  <option>No models found</option>
                ) : (
                  models.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))
                )}
              </select>
            </div>

            <button className="mcp-clear-btn" onClick={clearHistory} title="Clear chat">
              <span>🗑️</span>
              <span>Clear</span>
            </button>
          </div>
        </header>

        {/* Messages Container - Fixed Scrolling */}
        <div className="mcp-messages-wrapper">
          <div className="mcp-messages" ref={messagesContainerRef}>
            {messages.length === 0 ? (
              <div className="mcp-empty-state">
                <div className="mcp-empty-icon">💬</div>
                <h2>Welcome to MCP Agent</h2>
                <p>Connect an MCP server to enable tools, then start chatting. The agent will automatically use available tools to help you.</p>
                
                <div className="mcp-quick-prompts">
                  <span className="mcp-quick-label">Try asking:</span>
                  <div className="mcp-quick-grid">
                    {quickPrompts.map((prompt, i) => (
                      <button 
                        key={i}
                        className="mcp-quick-btn"
                        onClick={() => setInput(prompt.text)}
                      >
                        <span className="mcp-quick-icon">{prompt.icon}</span>
                        <span>{prompt.text}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {servers.length === 0 && (
                  <div className="mcp-empty-hint">
                    <span>👈</span>
                    <span>Start by connecting a server from the sidebar</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="mcp-messages-list">
                {messages.map((msg, i) => (
                  <MCPMessage key={i} message={msg} />
                ))}
                
                {isLoading && (
                  <div className="mcp-message mcp-message-assistant mcp-message-loading">
                    <div className="mcp-message-avatar">
                      <span>🤖</span>
                    </div>
                    <div className="mcp-message-content">
                      <div className="mcp-typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="mcp-input-wrapper">
          <form className="mcp-input-area" onSubmit={handleSubmit}>
            <div className="mcp-input-container">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything... (Shift+Enter for new line)"
                rows={1}
                disabled={isLoading}
              />
              <button 
                type="submit" 
                disabled={!input.trim() || isLoading}
                className="mcp-send-btn"
              >
                {isLoading ? (
                  <span className="mcp-send-loading">⏳</span>
                ) : (
                  <span>➤</span>
                )}
              </button>
            </div>
            <div className="mcp-input-footer">
              <span className="mcp-input-hint">
                {tools.length > 0 
                  ? `${tools.length} tools available • Press Enter to send`
                  : 'Connect a server to enable tools'
                }
              </span>
            </div>
          </form>
        </div>
      </main>

      <style>{`
        /* ===== Base Layout ===== */
        .mcp-chat-page {
          display: flex;
          height: 100vh;
          width: 100%;
          background: #09090b;
          color: #e4e4e7;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          position: relative;
          overflow: hidden;
        }

        /* ===== Animated Background ===== */
        .mcp-bg-gradient {
          position: absolute;
          inset: 0;
          background: 
            radial-gradient(ellipse at 0% 0%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 100% 100%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        .mcp-bg-grid {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 50px 50px;
          pointer-events: none;
          z-index: 0;
        }

        /* ===== Sidebar ===== */
        .mcp-sidebar {
          width: 320px;
          min-width: 320px;
          background: rgba(17, 17, 19, 0.95);
          backdrop-filter: blur(20px);
          border-right: 1px solid rgba(255,255,255,0.06);
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 10;
          transition: margin-left 0.3s ease, opacity 0.3s ease;
        }

        .mcp-sidebar.closed {
          margin-left: -320px;
          opacity: 0;
        }

        .mcp-sidebar-header {
          padding: 20px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .mcp-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        .mcp-logo-icon {
          font-size: 24px;
          filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.5));
        }

        .mcp-logo-text {
          font-size: 18px;
          font-weight: 700;
          background: linear-gradient(135deg, #10b981, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .mcp-badge-container {
          display: flex;
          gap: 8px;
        }

        .mcp-badge {
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
        }

        .mcp-badge.servers {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .mcp-badge.tools {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .mcp-sidebar-content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .mcp-sidebar-content::-webkit-scrollbar {
          width: 6px;
        }

        .mcp-sidebar-content::-webkit-scrollbar-track {
          background: transparent;
        }

        .mcp-sidebar-content::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
        }

        .mcp-sidebar-footer {
          padding: 16px 20px;
          border-top: 1px solid rgba(255,255,255,0.06);
          background: rgba(0,0,0,0.2);
        }

        .mcp-model-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .mcp-model-label {
          font-size: 11px;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .mcp-model-name {
          font-size: 12px;
          color: #10b981;
          font-family: 'JetBrains Mono', monospace;
        }

        /* ===== Sidebar Toggle ===== */
        .mcp-sidebar-toggle {
          position: absolute;
          left: ${showSidebar ? '320px' : '0'};
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          background: rgba(17, 17, 19, 0.95);
          border: 1px solid rgba(255,255,255,0.1);
          border-left: none;
          color: #71717a;
          padding: 12px 8px;
          border-radius: 0 8px 8px 0;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .mcp-sidebar-toggle:hover {
          color: #10b981;
          background: rgba(16, 185, 129, 0.1);
        }

        /* ===== Main Content ===== */
        .mcp-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          position: relative;
          z-index: 1;
          height: 100vh;
          overflow: hidden;
        }

        /* ===== Header ===== */
        .mcp-header {
          padding: 16px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: rgba(17, 17, 19, 0.8);
          backdrop-filter: blur(10px);
          flex-shrink: 0;
        }

        .mcp-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mcp-header-left h1 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .mcp-status-indicator {
          font-size: 12px;
          color: #f59e0b;
          background: rgba(245, 158, 11, 0.1);
          padding: 4px 10px;
          border-radius: 20px;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        .mcp-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mcp-model-select {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mcp-model-select label {
          color: #71717a;
          font-size: 13px;
        }

        .mcp-model-select select {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: #e4e4e7;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mcp-model-select select:hover {
          border-color: rgba(255,255,255,0.2);
        }

        .mcp-model-select select:focus {
          outline: none;
          border-color: #10b981;
        }

        .mcp-clear-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.1);
          color: #a1a1aa;
          padding: 8px 14px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s;
        }

        .mcp-clear-btn:hover {
          border-color: #ef4444;
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }

        /* ===== Messages Area - FIXED SCROLLING ===== */
        .mcp-messages-wrapper {
          flex: 1;
          min-height: 0;
          overflow: hidden;
          position: relative;
        }

        .mcp-messages {
          position: absolute;
          inset: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 24px;
          scroll-behavior: smooth;
        }

        .mcp-messages::-webkit-scrollbar {
          width: 8px;
        }

        .mcp-messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .mcp-messages::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }

        .mcp-messages::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }

        .mcp-messages-list {
          max-width: 900px;
          margin: 0 auto;
        }

        /* ===== Empty State ===== */
        .mcp-empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
          padding: 40px;
        }

        .mcp-empty-icon {
          font-size: 64px;
          margin-bottom: 20px;
          filter: drop-shadow(0 0 20px rgba(16, 185, 129, 0.3));
        }

        .mcp-empty-state h2 {
          font-size: 24px;
          font-weight: 600;
          margin: 0 0 12px;
          background: linear-gradient(135deg, #e4e4e7, #a1a1aa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .mcp-empty-state p {
          color: #71717a;
          font-size: 14px;
          max-width: 400px;
          line-height: 1.6;
          margin: 0 0 32px;
        }

        .mcp-quick-prompts {
          width: 100%;
          max-width: 500px;
        }

        .mcp-quick-label {
          display: block;
          font-size: 12px;
          color: #52525b;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .mcp-quick-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .mcp-quick-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          color: #a1a1aa;
          padding: 14px 16px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 13px;
          text-align: left;
          transition: all 0.2s;
        }

        .mcp-quick-btn:hover {
          background: rgba(16, 185, 129, 0.1);
          border-color: rgba(16, 185, 129, 0.3);
          color: #10b981;
          transform: translateY(-2px);
        }

        .mcp-quick-icon {
          font-size: 18px;
        }

        .mcp-empty-hint {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 32px;
          padding: 12px 20px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 10px;
          color: #60a5fa;
          font-size: 13px;
        }

        /* ===== Loading Message ===== */
        .mcp-message-loading .mcp-message-avatar {
          background: linear-gradient(135deg, #10b981, #059669);
        }

        .mcp-typing-indicator {
          display: flex;
          gap: 5px;
          padding: 8px 0;
        }

        .mcp-typing-indicator span {
          width: 8px;
          height: 8px;
          background: #10b981;
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out;
        }

        .mcp-typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .mcp-typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }

        /* ===== Input Area ===== */
        .mcp-input-wrapper {
          padding: 16px 24px 24px;
          background: linear-gradient(to top, rgba(9, 9, 11, 0.95), transparent);
          flex-shrink: 0;
        }

        .mcp-input-area {
          max-width: 900px;
          margin: 0 auto;
        }

        .mcp-input-container {
          display: flex;
          gap: 12px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 8px;
          transition: all 0.2s;
        }

        .mcp-input-container:focus-within {
          border-color: rgba(16, 185, 129, 0.5);
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .mcp-input-container textarea {
          flex: 1;
          background: transparent;
          border: none;
          color: #e4e4e7;
          padding: 10px 14px;
          font-size: 14px;
          font-family: inherit;
          resize: none;
          line-height: 1.5;
          max-height: 150px;
        }

        .mcp-input-container textarea:focus {
          outline: none;
        }

        .mcp-input-container textarea::placeholder {
          color: #52525b;
        }

        .mcp-send-btn {
          align-self: flex-end;
          background: linear-gradient(135deg, #10b981, #059669);
          border: none;
          color: white;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .mcp-send-btn:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
        }

        .mcp-send-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .mcp-send-loading {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .mcp-input-footer {
          display: flex;
          justify-content: center;
          margin-top: 10px;
        }

        .mcp-input-hint {
          font-size: 11px;
          color: #52525b;
        }

        /* ===== Responsive ===== */
        @media (max-width: 768px) {
          .mcp-sidebar {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            z-index: 100;
          }

          .mcp-sidebar.closed {
            margin-left: -320px;
          }

          .mcp-quick-grid {
            grid-template-columns: 1fr;
          }

          .mcp-header {
            padding: 12px 16px;
          }

          .mcp-messages {
            padding: 16px;
          }

          .mcp-input-wrapper {
            padding: 12px 16px 16px;
          }
        }
      `}</style>
    </div>
  );
}